import {
  PartyData,
  PartyPizza,
  PartySession,
  PizzaSelection,
  Season,
} from '@types';
import { AlertModal, Card, EmptyState, LoadingSpinner } from '_atoms';
import { SeasonDetailsService } from '_services';
import PartyService from '_services/PartyService';
import React, { useCallback, useEffect, useState } from 'react';
import {
  MdAdd,
  MdArrowBack,
  MdExpandMore,
  MdInfo,
  MdPartyMode,
  MdRemove,
} from 'react-icons/md';
import { PiPizzaDuotone } from 'react-icons/pi';

interface SelectPartyPackageProps {
  selectedPartyPackage?: PartySession;
  selectedPizzas?: PizzaSelection[];
  onNext: (data?: Partial<PartyData>) => void;
  onPrevious?: () => void;
  onStepComplete: (data: Partial<PartyData>) => void;
}

export const SelectPartyPackage: React.FC<SelectPartyPackageProps> = ({
  selectedPartyPackage,
  selectedPizzas = [],
  onNext,
  onPrevious,
  onStepComplete,
}) => {
  const [partyPackages, setPartyPackages] = useState<PartySession[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingInitial, setIsLoadingInitial] = useState(true);
  const [selectedPackage, setSelectedPackage] = useState<
    PartySession | undefined
  >(selectedPartyPackage);
  const [selectedPizzaList, setSelectedPizzaList] =
    useState<PizzaSelection[]>(selectedPizzas);
  const [isPackageSelectionCollapsed, setIsPackageSelectionCollapsed] =
    useState(false);
  const [settings, setSettings] = useState<Season>({
    id: '',
    startDate: '',
    endDate: '',
    isTicketPurchasesEnabled: false,
    isSeasonPassPurchasesEnabled: false,
    isPartyPackagePurchasesEnabled: false,
    isSkatingLessonPurchasesEnabled: false,
    isHockeyLessonPurchasesEnabled: false,
    sessionTimes: [],
  });
  const [alertModal, setAlertModal] = useState<{
    isOpen: boolean;
    type: 'info' | 'error' | 'success' | 'warning';
    title: string;
    message: string;
  }>({
    isOpen: false,
    type: 'info',
    title: '',
    message: '',
  });

  const pizzaTypeImages: { [key: string]: string } = {
    large_combination:
      'https://modesto-on-ice-prod.s3.us-west-1.amazonaws.com/prod/birthday-extra-10.jpg',
    large_pepperoni:
      'https://modesto-on-ice-prod.s3.us-west-1.amazonaws.com/prod/birthday-extra-7.jpg',
    large_veggie:
      'https://modesto-on-ice-prod.s3.us-west-1.amazonaws.com/prod/birthday-extra-11.jpg',
    large_cheese:
      'https://modesto-on-ice-prod.s3.us-west-1.amazonaws.com/prod/birthday-extra-2.jpg',
  };

  const showAlert = useCallback(
    (
      type: 'info' | 'error' | 'success' | 'warning',
      title: string,
      message: string,
    ) => {
      setAlertModal({ isOpen: true, type, title, message });
    },
    [],
  );

  const loadPartyPackages = useCallback(async () => {
    setIsLoading(true);
    try {
      const responseData = await PartyService.getPariesList();
      setPartyPackages((responseData as unknown as PartySession[]) || []);
    } catch (error) {
      console.error('Error loading party packages:', error);
      showAlert(
        'error',
        'Error',
        'Failed to load party packages. Please try again.',
      );
    } finally {
      setIsLoading(false);
    }
  }, [showAlert]);

  useEffect(() => {
    loadCurrentSeason();
    loadPartyPackages();
  }, [loadPartyPackages]);

  const loadCurrentSeason = async () => {
    setIsLoadingInitial(true);
    try {
      const responseData =
        (await SeasonDetailsService.getCurrentSeasonDetails()) as unknown as Season;

      if (responseData) {
        setSettings(responseData);
      }
    } catch (error) {
      console.error('Error loading current season:', error);
    } finally {
      setIsLoadingInitial(false);
    }
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, type: 'info', title: '', message: '' });
  };

  const handlePackageSelect = (partyPackage: PartySession) => {
    setSelectedPackage(partyPackage);
    // Reset selected pizzas when changing package
    setSelectedPizzaList([]);
    // Collapse the package selection to focus on pizza selection
    setIsPackageSelectionCollapsed(partyPackage.pizzaLimit > 0);
    onStepComplete({
      selectedPartyPackage: partyPackage,
      selectedPizzas: [],
    });
  };

  const togglePackageSelection = () => {
    setIsPackageSelectionCollapsed(!isPackageSelectionCollapsed);
  };

  const handlePizzaQuantityChange = (pizza: PartyPizza, change: number) => {
    if (!selectedPackage) return;

    const currentTotal = selectedPizzaList.reduce(
      (sum, selection) => sum + selection.quantity,
      0,
    );
    const existingSelection = selectedPizzaList.find(
      p => p.pizza.id === pizza.id,
    );
    const currentQuantity = existingSelection?.quantity || 0;
    const newQuantity = currentQuantity + change;

    // Check if we're within the pizza limit
    if (newQuantity < 0) return; // Can't go below 0
    if (
      currentTotal - currentQuantity + newQuantity >
      selectedPackage.pizzaLimit
    ) {
      showAlert(
        'warning',
        'Pizza Limit Reached',
        `You can only select up to ${selectedPackage.pizzaLimit} pizzas for this package.`,
      );
      return;
    }

    let updatedPizzas: PizzaSelection[];

    if (newQuantity === 0) {
      // Remove pizza from selection
      updatedPizzas = selectedPizzaList.filter(p => p.pizza.id !== pizza.id);
    } else if (existingSelection) {
      // Update existing selection
      updatedPizzas = selectedPizzaList.map(p =>
        p.pizza.id === pizza.id ? { ...p, quantity: newQuantity } : p,
      );
    } else {
      // Add new selection
      updatedPizzas = [...selectedPizzaList, { pizza, quantity: newQuantity }];
    }

    setSelectedPizzaList(updatedPizzas);
    onStepComplete({
      selectedPartyPackage: selectedPackage,
      selectedPizzas: updatedPizzas,
    });
  };

  const handleContinue = () => {
    if (selectedPackage) {
      onNext({
        selectedPartyPackage: selectedPackage,
        selectedPizzas: selectedPizzaList,
      });
    }
  };

  const NotInSeason = () => (
    <div className="flex items-center justify-center min-h-[400px]">
      <Card className="w-full max-w-md justify-center items-center border-none">
        <div className="text-center space-y-4">
          <MdInfo className="mx-auto text-gray-400 text-6xl" />
          <div>
            <h3 className="text-xl font-semibold text-gray-900 mb-2">
              Party Packages Not Available
            </h3>
            <p className="text-gray-600">
              Party package purchases are currently not enabled. Please check
              back later or contact us for more information.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {!settings.isPartyPackagePurchasesEnabled && !isLoadingInitial ? (
        <NotInSeason />
      ) : (
        <>
          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-gray-900">
              {selectedPackage && isPackageSelectionCollapsed
                ? 'Select Your Pizzas'
                : 'Select Party Package'}
            </h2>
            <p className="text-gray-600 text-lg">
              {selectedPackage && isPackageSelectionCollapsed
                ? 'Choose the pizzas for your party'
                : 'Choose the perfect party package for your celebration'}
            </p>
          </div>

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <LoadingSpinner />
            </div>
          ) : partyPackages.length === 0 ? (
            <EmptyState
              title="No Party Packages Available"
              description="There are currently no party packages available for booking."
              icon={MdPartyMode}
            />
          ) : (
            <div className="space-y-6">
              {/* Selected Package Summary (when collapsed) */}
              {selectedPackage && isPackageSelectionCollapsed && (
                <Card className="bg-blue-50 border-blue-200">
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <MdPartyMode className="w-6 h-6 text-blue-600" />
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">
                            {selectedPackage.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            ${selectedPackage.price}
                            {selectedPackage.partyPizzas &&
                              Array.isArray(selectedPackage.partyPizzas) &&
                              selectedPackage.partyPizzas.length > 0 &&
                              ` • Pizza Limit: ${selectedPackage.pizzaLimit}`}
                          </p>
                        </div>
                      </div>
                      <button
                        onClick={togglePackageSelection}
                        className="inline-flex items-center px-3 py-2 text-sm font-medium text-blue-600 hover:text-blue-800 transition-colors"
                      >
                        Change Package
                        <MdExpandMore className="ml-1 w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </Card>
              )}

              {/* Full Package Selection (when expanded) */}
              {!isPackageSelectionCollapsed && (
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  {partyPackages.map(partyPackage => (
                    <div
                      key={partyPackage.id}
                      onClick={() => handlePackageSelect(partyPackage)}
                      className="cursor-pointer"
                    >
                      <Card
                        className={`w-full h-full transition-all duration-200 hover:shadow-lg ${
                          selectedPackage?.id === partyPackage.id
                            ? 'ring-2 ring-blue-500 bg-blue-50'
                            : 'hover:shadow-md'
                        }`}
                      >
                        <div className="p-0">
                          <div className="flex-1 items-center mb-4">
                            <div className="text-center flex flex-col items-center">
                              <h3 className="text-lg mb-4 font-semibold text-gray-900">
                                {partyPackage.name}
                              </h3>
                              <span className="text-3xl font-bold text-blue-600">
                                ${partyPackage.price}
                              </span>
                            </div>
                          </div>

                          <div
                            className="text-gray-600 text-sm mb-4 prose prose-sm max-w-none [&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_p]:mb-2"
                            dangerouslySetInnerHTML={{
                              __html: partyPackage.description,
                            }}
                          />

                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <div className="flex items-center justify-between">
                              {selectedPackage?.id === partyPackage.id && (
                                <div className="flex items-center text-blue-600">
                                  <span className="text-sm font-medium">
                                    Selected
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>

                          {partyPackage.disclaimer && (
                            <div className="mt-3 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                              <p className="text-xs text-yellow-800">
                                {partyPackage.disclaimer}
                              </p>
                            </div>
                          )}
                        </div>
                      </Card>
                    </div>
                  ))}
                </div>
              )}

              {/* Pizza Selection - Only show if package has available pizzas */}
              {selectedPackage &&
                isPackageSelectionCollapsed &&
                selectedPackage.partyPizzas &&
                Array.isArray(selectedPackage.partyPizzas) &&
                selectedPackage.partyPizzas.length > 0 && (
                  <div className="space-y-4">
                    <div className="text-center space-y-2">
                      <p className="text-gray-600">
                        Choose {selectedPackage.pizzaLimit} pizza
                        {selectedPackage.pizzaLimit !== 1 ? 's' : ''} for your
                        party
                        {selectedPizzaList.length > 0 && (
                          <span className="ml-2 text-blue-600 font-medium">
                            (
                            {selectedPizzaList.reduce(
                              (sum, selection) => sum + selection.quantity,
                              0,
                            )}
                            /{selectedPackage.pizzaLimit} selected)
                          </span>
                        )}
                      </p>
                    </div>

                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                      {selectedPackage.partyPizzas.map(pizza => {
                        const currentSelection = selectedPizzaList.find(
                          p => p.pizza.id === pizza.id,
                        );
                        const currentQuantity = currentSelection?.quantity || 0;
                        const totalSelected = selectedPizzaList.reduce(
                          (sum, selection) => sum + selection.quantity,
                          0,
                        );
                        const canIncrease =
                          totalSelected < selectedPackage.pizzaLimit;

                        return (
                          <div key={pizza.id}>
                            <Card className="w-full h-full transition-all duration-200 hover:shadow-md">
                              <div className="p-4 items-center">
                                {pizza.type && pizzaTypeImages[pizza.type] && (
                                  <div className="mb-4">
                                    <img
                                      src={pizzaTypeImages[pizza.type]}
                                      alt={pizza.name}
                                      className="w-full h-32 object-contain rounded-lg"
                                      onError={e => {
                                        e.currentTarget.style.display = 'none';
                                      }}
                                    />
                                  </div>
                                )}

                                <div className="flex items-center mb-3">
                                  <div className="w-5 h-5 bg-orange-100 rounded-lg flex items-center justify-center mr-3">
                                    <PiPizzaDuotone className="w-5 h-5 text-orange-600" />
                                  </div>
                                  <div className="flex-1">
                                    <h4 className="text-lg font-semibold text-gray-900">
                                      {pizza.name}
                                    </h4>
                                  </div>
                                </div>

                                <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                                  {pizza.description}
                                </p>

                                <div className="items-center">
                                  {/* Quantity Controls */}
                                  <div className="flex items-center">
                                    <button
                                      onClick={() =>
                                        handlePizzaQuantityChange(pizza, -1)
                                      }
                                      disabled={currentQuantity === 0}
                                      className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                                    >
                                      <MdRemove className="w-4 h-4 text-gray-600" />
                                    </button>

                                    <span className="w-8 text-center font-medium text-gray-900">
                                      {currentQuantity}
                                    </span>

                                    <button
                                      onClick={() =>
                                        handlePizzaQuantityChange(pizza, 1)
                                      }
                                      disabled={!canIncrease}
                                      className="w-8 h-8 rounded-full bg-blue-100 hover:bg-blue-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center transition-colors"
                                    >
                                      <MdAdd className="w-4 h-4 text-blue-600" />
                                    </button>
                                  </div>
                                </div>
                              </div>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

              {/* Navigation Buttons */}
              <div className="flex justify-between pt-6">
                {onPrevious && (
                  <button
                    onClick={onPrevious}
                    className="inline-flex items-center px-4 py-3 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-all duration-200"
                  >
                    <MdArrowBack className="mr-2" />
                    Back
                  </button>
                )}
                <div className="flex-1" />
                <button
                  onClick={handleContinue}
                  disabled={
                    !selectedPackage ||
                    (selectedPackage &&
                      selectedPackage.partyPizzas &&
                      Array.isArray(selectedPackage.partyPizzas) &&
                      selectedPackage.partyPizzas.length > 0 &&
                      selectedPackage.pizzaLimit > 0 &&
                      selectedPizzaList.reduce(
                        (sum, selection) => sum + selection.quantity,
                        0,
                      ) !== selectedPackage.pizzaLimit)
                  }
                  className="inline-flex items-center px-6 sm:px-8 py-4 sm:py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 shadow-sm hover:shadow-md text-base sm:text-sm"
                >
                  Continue
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Alert Modal */}
      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        title={alertModal.title}
        message={alertModal.message}
      />
    </div>
  );
};
